/**
 * Usage Service
 * Handles usage statistics API operations.
 */

let USAGE_API_URL = ""

async function loadUsageApiUrl(): Promise<string> {
  if (USAGE_API_URL) return USAGE_API_URL
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  const baseUrl = config.feedbackApiUrl
  if (!baseUrl) throw new Error("API URL not found")
  USAGE_API_URL = `${baseUrl.replace(/\/?$/, "/")}`
  return USAGE_API_URL
}

async function fetchWithAuth(
  url: string,
  idToken: string,
  options: RequestInit = {}
) {
  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    },
  })
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error?.message || `HTTP error! status: ${response.status}`
    )
  }
  return response.json()
}

// --- Types ---

export interface UsageSummary {
  totalRequests: number
  totalInputTokens: number
  totalOutputTokens: number
  totalTokens: number
  totalToolCalls: number
  /** 期間内の合計クレジット（実コスト建ての統一単位。1 credit = USD 0.001）。 */
  totalCredits: number
}

export interface UserUsage {
  userId: string
  totalTokens: number
  requestCount: number
  inputTokens: number
  outputTokens: number
  /** このユーザーの期間内合計クレジット。 */
  credits: number
}

export interface DailyUsage {
  date: string
  inputTokens: number
  outputTokens: number
  requestCount: number
  toolCallCount: number
  /** この日の合計クレジット。 */
  credits: number
}

/** 本人スコープの利用状況（GET /usage/me のレスポンス）。 */
export interface MyUsage {
  /** 本人のロール。 */
  role: "owner" | "admin" | "member" | "viewer"
  /** 本日の利用状況。 */
  today: {
    /** 本日の消費クレジット（整数）。 */
    credits: number
    /** 本人ロールの日次クレジット上限（0 のとき上限なし扱い）。 */
    limit: number
    /** 本日のリクエスト数。 */
    requests: number
  }
  /** テナント月次の利用状況。 */
  tenantMonthly: {
    /** 対象月（YYYY-MM）。 */
    month: string
    /** 当月の消費クレジット（整数）。 */
    credits: number
    /** テナント月次上限。null/0 のとき未設定（＝上限なし）。 */
    limit: number | null
    /** 猶予クレジット数。 */
    grace: number
    /** 遮断中か（grace 超過）。 */
    blocked: boolean
  }
}

// --- Endpoints ---

/**
 * 本人スコープの利用状況を取得する。
 * @param idToken Cognito の idToken（Authorization: Bearer に使用）。
 * @returns 本人ロール・本日・テナント月次の利用状況。
 */
export async function getMyUsage(idToken: string): Promise<MyUsage> {
  const url = await loadUsageApiUrl()
  return fetchWithAuth(`${url}usage/me`, idToken)
}

export async function getUsageSummary(
  idToken: string,
  from: string,
  to: string
): Promise<UsageSummary> {
  const url = await loadUsageApiUrl()
  const params = new URLSearchParams({ from, to })
  return fetchWithAuth(`${url}admin/usage/summary?${params}`, idToken)
}

export async function getUsageByUser(
  idToken: string,
  from: string,
  to: string,
  limit: number = 20
): Promise<{ users: UserUsage[] }> {
  const url = await loadUsageApiUrl()
  const params = new URLSearchParams({ from, to, limit: String(limit) })
  return fetchWithAuth(`${url}admin/usage/by-user?${params}`, idToken)
}

export async function getUsageDaily(
  idToken: string,
  from: string,
  to: string,
  userId?: string
): Promise<{ daily: DailyUsage[] }> {
  const url = await loadUsageApiUrl()
  const params = new URLSearchParams({ from, to })
  if (userId) params.set("userId", userId)
  return fetchWithAuth(`${url}admin/usage/daily?${params}`, idToken)
}
